/**
 * FILE: packages\utils\src\checkout-phone.ts
 * PURPOSE: Shared business utility.
 * AGENT_CONTEXT: Pure functions. No React, no Supabase client imports.
 * LAST_REVIEWED: 2026-05-07
 */

/**
 * Checkout phone normalization — single spec for website sync + manual enroll.
 * PK storage: digits only (E.164 without +). country_code stored separately.
 */

import { cleanPhone, validatePhone } from './phone.ts'
import { normalizeDialCode, resolveDialCodeFromCountry } from './country-dial-codes.ts'

const INDIA_COUNTRY_VALUES = new Set(['india', 'in', 'ind'])

export interface CheckoutPhoneInput {
  rawPhone: string
  addressCountry?: string | null
  addressCountryCode?: string | null
  currency?: string | null
}

export type CheckoutPhoneSuccess = {
  ok: true
  phone: string
  countryCode: string | null
  isInternational: boolean
  isNri?: boolean
}

export type CheckoutPhoneFailure = {
  ok: false
  error: 'invalid_phone'
}

export type CheckoutPhoneResult = CheckoutPhoneSuccess | CheckoutPhoneFailure

function isExplicitIndiaCountry(country: string | null | undefined): boolean {
  if (!country?.trim()) return false
  return INDIA_COUNTRY_VALUES.has(country.trim().toLowerCase())
}

/** Empty country + INR is treated as India; empty + non-INR is not. */
function isIndiaCheckoutContext(country: string | null | undefined, currency: string | null): boolean {
  if (isExplicitIndiaCountry(country)) return true
  if (!country?.trim() && currency === 'INR') return true
  return false
}

/** Strip +, spaces, dashes, parentheses, leading 00, and a single trunk 0 prefix. */
export function stripCheckoutPhone(raw: string): string {
  let s = raw.trim().replace(/[\s+\-().]/g, '')
  if (s.startsWith('00')) s = s.slice(2)
  if (s.startsWith('0') && s.length > 1) s = s.slice(1)
  return s.replace(/\D/g, '')
}

/**
 * Combine a digit string with a dial code, normalising:
 * - duplicate country-code prefix
 * - leading zeros after the country code
 * - trunk-zero prefix when no dial code is present
 */
function applyDialCode(digits: string, dialCode: string): string {
  const code = dialCode.replace(/\D/g, '')
  let cleaned = digits.replace(/\D/g, '').replace(/^0+/, '')
  if (!cleaned) return code
  if (!code) return cleaned

  if (cleaned.startsWith(code)) {
    cleaned = cleaned.slice(code.length).replace(/^0+/, '')
    // Handle double country-code prefix (e.g. +91 +91 98765...)
    if (cleaned.startsWith(code)) {
      cleaned = cleaned.slice(code.length).replace(/^0+/, '')
    }
  }

  return `${code}${cleaned}`
}

function isValidPkLength(digits: string): boolean {
  return digits.length >= 10 && digits.length <= 15
}

export function normalizeCheckoutPhone(input: CheckoutPhoneInput): CheckoutPhoneResult {
  const rawPhone = input.rawPhone?.trim() ?? ''
  if (!rawPhone) return { ok: false, error: 'invalid_phone' }

  const currency = input.currency?.trim().toUpperCase() || null
  const addressCountry = input.addressCountry?.trim() || null

  let dialCode = normalizeDialCode(input.addressCountryCode ?? '')
  if (!dialCode && addressCountry) {
    dialCode = resolveDialCodeFromCountry(addressCountry) ?? ''
  }

  const indian = cleanPhone(rawPhone)
  const digits = stripCheckoutPhone(rawPhone)

  if (indian && validatePhone(indian) && currency === 'USD') {
    return { ok: true, phone: indian, countryCode: '91', isInternational: false, isNri: true }
  }

  if (indian && validatePhone(indian) && isIndiaCheckoutContext(addressCountry, currency)) {
    return { ok: true, phone: indian, countryCode: '91', isInternational: false }
  }

  if (dialCode && digits) {
    const full = applyDialCode(digits, dialCode)
    if (isValidPkLength(full)) {
      return { ok: true, phone: full, countryCode: dialCode, isInternational: true }
    }
    return { ok: false, error: 'invalid_phone' }
  }

  if (isValidPkLength(digits)) {
    const cc = dialCode || (digits.length > 10 ? digits.slice(0, digits.length - 10) : '')
    // No dial code context + valid Indian 10-digit → treat as Indian (matches old normalizePaymentPhone
    // behaviour and avoids storing countryCode='' which ?? cannot distinguish from null).
    if (!cc && indian && validatePhone(indian)) {
      return { ok: true, phone: indian, countryCode: '91', isInternational: false }
    }
    return {
      ok: true,
      phone: digits,
      countryCode: cc || null,
      isInternational: Boolean(dialCode) || digits.length > 10,
    }
  }

  if (indian && validatePhone(indian)) {
    return { ok: true, phone: indian, countryCode: '91', isInternational: false }
  }

  return { ok: false, error: 'invalid_phone' }
}

/** Format for UI: +{country_code} {national segment}. */
export function formatPhoneDisplay(countryCode: string | null | undefined, phone: string): string {
  const cc = normalizeDialCode(countryCode ?? '')
  const digits = phone.replace(/\D/g, '')
  if (!digits) return phone
  if (!cc) return `+${digits}`
  const national = digits.startsWith(cc) ? digits.slice(cc.length) : digits
  if (national.length <= 4) return `+${cc} ${digits}`
  const mid = Math.ceil(national.length / 2)
  return `+${cc} ${national.slice(0, mid)} ${national.slice(mid)}`
}

/** Format paid amount with ISO currency — no conversion. */
export function formatPaidAmount(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null) return '—'
  const code = (currency ?? 'INR').toUpperCase()
  const formatted = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: code === 'INR' ? 0 : 2,
  }).format(amount)
  return `${code} ${formatted}`
}
